/* ── GROUPS ARE NOT CARDS (#1991, ruling 72563 §4) ──────────────────────────
 *
 * *"Use quiet uppercase group labels; each destination is one icon + one
 * readable label. Avoid a pill for every row"* — and the ruling opens by
 * refusing *"the current Console wall of similarly weighted pills/chevrons"*.
 *
 * What made every group a card was two rules: an odd-child zebra tint and a
 * border that appeared on open. Both gave fifteen groups identical visual
 * weight and turned a scan down the bar into a stack of boxes, which is what
 * the ruling rejects. Separation is the JOINT now (#2406 navbar sheet, below)
 * — one cut line with a lit lip — which is the quiet form and costs one pixel.
 *
 * The 3px pad went with them: it existed to hold the tint off the rows, and a
 * raw length in padding is debt enforcement.test.ts counts. data-open stays as
 * a hook (the editor drag-hover and the browser matrix read it); it no longer
 * paints. */
.group { margin: 0; padding: var(--sp-2) 0 var(--sp-1); position: relative; transition: background 90ms ease-out; }
/* ── THE JOINT, AND THE WASH (#2406 navbar sheet, marked LOCKED) ───────────
 * *"Sections are divided by a joint: one cut line plus a light lip. Never a
 * fill."* — the cut line with its lit lip on the side it faces, between
 * sections only; the first section has no orphan rule above it. The lip is
 * the sheet's own `0 -1px 0`: it catches the light on the section ABOVE the
 * cut, which is what makes the line read as a groove rather than a stroke.
 *
 * The joint replaced the space between sections. Rhythm used to be a 16px
 * margin below every group AND a 12px pad above the next — a card stack drawn
 * in whitespace — and the sheet's sections sit flush, one joint apart, with
 * their own 8px/4px inset. Sixteen sections fit where eleven did.
 *
 * *"An open section carries a faint wash from the left edge, fading out by
 * 72% — it marks the section without boxing it."* The wash is a gradient, not
 * a band: it has no edge on the right to read as a plate. It runs from the
 * column's own left edge, which is why the scroller pads nothing sideways
 * (`ConsoleNavigationShell.module.css`). */
.group + .group {
  border-top: 1px solid var(--hair);
  box-shadow: 0 -1px 0 var(--rim);
}
.group[data-open] {
  background: linear-gradient(
    to right,
    color-mix(in srgb, var(--ink) 3%, transparent),
    transparent 72%
  );
}
/* *"One chisel line per open section, inset 23px, running from the first item
   to the last."* It replaces the container an open section would otherwise
   need: everything indented to it belongs to the section above.

   THE SHEET'S GEOMETRY, and it corrects the last pass. That pass hung each
   item's BOX on the chisel and drew the vein as the box's own left border, so
   a hovered row's tone ran over the line it hung from. The sheet does the
   opposite: the list is indented 23px, the line is drawn at the list's left
   edge, the items begin one step (`--nav-hang`) further in, and the active
   vein reaches BACK from the item to lie on the line. So the chisel, the vein
   and the section mark's centre are one vertical, and a hover band never
   crosses it. `--nav-chisel` and `--nav-hang` are the two numbers both sides
   read; the vein's offset is the hang, negated, so they cannot drift. */
.items {
  position: relative;
  --nav-chisel: 23px;
  --nav-hang: 13px;
  margin-left: var(--nav-chisel);
  padding: 0 0 0 var(--nav-hang);
}
.group[data-open] > .items::before {
  content: '';
  position: absolute;
  left: 0;
  top: 1px;
  bottom: 5px;
  width: 1px;
  background: var(--hair);
  box-shadow: 1px 0 0 var(--rim);
}
/* ── THE SECTION LABEL (#2406 navbar sheet) ─────────────────────────────────
 * The section head — the engraved label, the section glyph and the chevron
 * that rotates on open — is the kit's `NavigationSectionHead` since #2406
 * (lane b3-shell), which took this file's `.mod` rules verbatim. The flyout's
 * head below shares its "you are here" brand ink. */
.flyIcon { flex: 0 0 auto; color: var(--brand-text); }
.readRow { position: relative; display: flex; align-items: center; min-width: 0; }
.readRow .readLink { flex: 1; min-width: 0; }
/* The row's Pin menu floats OVER the row's right end instead of standing
   beside it. Beside it, its control-height trigger set every row's height —
   36px where the sheet's rows are 28 — so a control that is invisible until
   hover decided the rhythm of the whole list. */
.readRow [data-testid='customize-row-menu'] {
  position: absolute; right: 0; top: 50%; transform: translateY(-50%);
}
.readRow [data-testid='customize-row-menu'] { opacity: 0; }
.readRow:hover [data-testid='customize-row-menu'],
.readRow:focus-within [data-testid='customize-row-menu'] { opacity: 1; }
/* ── THE DESTINATION IS THE KIT'S (#2624) ──────────────────────────────────
 * A destination — text hanging from the chisel, the tone step on hover and
 * press, and for the page on screen the brand text, weight 700 and the VEIN
 * on the chisel, with its forced-colours and reduced-motion arms and the
 * coarse floor — is drawn by `@solum/ui`'s `NavigationDestination`, which
 * took this file's `.item` / `.active` rules value for value. The portal drew
 * the same anatomy in its own sheet; both hosts draw the one primitive now.
 * This sheet keeps what it PLACES: `--nav-hang` on `.items` (the vein reads it
 * to land on the chisel) and `.readLink`'s share of the row above. */
/* The flyout's current destination is the console's own flyout copy (#2502
   A1's recorded hand-off, not this packet): brand text and weight, on the
   flyout's faint brand ground below. */
.flyActive {
  color: var(--brand-text);
  font-weight: 700;
  background: none;
}
@media (prefers-reduced-motion: reduce) {
  .group { transition: none; }
}

/* ── COLLAPSED: A WALL OF SECTION MARKS (#2406, Organization Detail) ───────
 * *"Collapsed, it is a 60px wall of section icons — the wall stays, the
 * courses reduce to their marks."* Each section is its mark, centred; the
 * joints stay (they are the wall's courses); the wash goes, because there is
 * no open section on a wall. The section that owns the current page keeps the
 * vein on the rail's edge, so place is never lost. */
.bar[data-collapsed] .group { padding: var(--sp-0-5) 0; background: none; }
.bar[data-collapsed] .items { display: none; }
.bar[data-collapsed] .group[data-current]::before {
  content: ''; position: absolute; left: 0; top: 6px; bottom: 6px;
  width: 2px; border-radius: 2px; background: var(--mat-vein);
}
/* The flyout: one collapsed section's destinations, seated beside the rail.
   It floats, so it keeps the tall shadow; its head is the section label,
   engraved, on the stone head tone with a joint under it. */
.flyout {
  position: fixed; z-index: var(--z-overlay);
  width: 232px; max-height: calc(100vh - var(--sp-4)); overflow-y: auto;
  background: var(--mat-slab); border-radius: var(--mat-r-control);
  box-shadow: var(--mat-sh-pop);
}
.flyHead {
  display: flex; align-items: center; gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border-bottom: 1px solid var(--hair); box-shadow: 0 1px 0 var(--rim);
  color: var(--muted); text-shadow: 0 1px 0 var(--rim);
  font-size: var(--fs-2xs); font-weight: 800; letter-spacing: 0.13em; text-transform: uppercase;
}
.flyBody { display: flex; flex-direction: column; padding: var(--sp-1); }
.flyItem {
  display: flex; align-items: center; min-height: 28px; padding: var(--sp-1) var(--sp-2);
  border-radius: var(--mat-r-inner); color: color-mix(in srgb, var(--ink) 82%, var(--surface));
  font-size: var(--fs-sm); font-weight: 600; text-decoration: none;
}
.flyItem:hover { background: color-mix(in srgb, var(--ink) 4.5%, transparent); color: var(--ink); }
/* The flyout's current destination shares the rail's active ink (above) and
   adds the faint brand ground the flyout's own sheet gives it: the flyout is a
   floating list, not the chisel, so there is no line for a vein to lie on. */
.flyItem.flyActive { background: color-mix(in srgb, var(--brand) 8%, transparent); }
.loading { padding: var(--sp-4); }

/* ── IN-PLACE EDIT MODE (ruling 50100) ─────────────────────────────────────
 *
 * The bar does not become a different surface when it becomes editable: every
 * class above still applies, and everything here is ADDITIVE. `.group` and
 * `.items` are the same rules the read-only bar uses, an edited row is drawn
 * by the kit's same `NavigationDestination` (#2624) and its head by the kit's
 * same section head (`NavigationSectionHeadRow`), which is what makes the
 * editable bar a faithful preview (row 2) rather than a second list that
 * resembles one.
 *
 * Tone, never a stroke — the same rule NavigationEditor.module.css records from
 * owner comment 41876. The two exceptions are the two it sanctions: a grabbed
 * row (selection, `--edge`) and a row in flight (`--clay-4`, the one elevation
 * step a transform may accompany). Neither appears in a `:focus` selector;
 * focus stays `--focus-ring` from global.css, which `focus-ring.test.ts` scans
 * every stylesheet in the repo to enforce.
 */

/* Visually hidden, never display:none — a display:none live region is not in
   the accessibility tree, so nothing would ever be announced. */
.srOnly {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; border: 0;
}

/* Add section sits ABOVE the scroll region for the reason the sheet's does:
   creating a section is an action on the whole bar, and a control that scrolls
   away with a 37-link list is a control most people never find. */
.editActions { display: flex; align-items: center; padding: 0 var(--sp-1) var(--sp-2); }

/* The SHARED column owns the scroller now (#1991, ruling 72563 §7), so this is
   no longer one: a scroller inside a scroller is two places a keyboard user
   can be lost in, and the identity/footer regions are outside both. The
   element stays because it carries the nav-edit-mode testid, which is how
   every spec in both hosts asks whether the bar is being edited. The
   shell-level mode bar is still fixed, outside all of it.

   NOT display:contents: an element with no box is not VISIBLE to Playwright,
   and those specs assert that this testid is. */
.editScroll { min-height: 0; }

/* A group header and an item carry the same six controls in edit mode, so both
   wrap the same way: the NAME yields first and the controls drop to a second
   line rather than being pushed off a 240-340px sidebar. */
.editHead, .editRow { flex-wrap: wrap; row-gap: var(--sp-1); }
.editHead { justify-content: flex-start; }
.editRow { cursor: default; border-left-color: transparent; }
.editLabel { flex: 1 1 6ch; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.editControls { display: flex; align-items: center; gap: var(--sp-1); margin-left: auto; }
.editDisclosure {
  flex: 1 1 8ch; min-width: 0; min-height: 36px; display: flex; align-items: center;
  justify-content: space-between; gap: var(--sp-2); padding: 0 var(--sp-2);
  color: inherit; text-align: left; border-radius: var(--r-sm);
}
.editDisclosure:hover { background: var(--surf-1); }
.editDisclosure svg { width: 15px; height: 15px; flex: 0 0 auto; }
.editName { flex: 1 1 8ch; min-width: 0; }
.editEmpty { margin: 0; padding: 7px var(--sp-3); color: var(--muted); font-size: var(--fs-sm); font-style: italic; }

/* `cursor: grab` is the whole discoverability affordance for the pointer route;
   the keyboard route is announced by the grip's own `aria-describedby`, because
   a cursor shape says nothing to a screen reader. */
.grip { flex: 0 0 auto; cursor: grab; }
.grip[aria-pressed='true'] { cursor: grabbing; }

/* Hidden reads as dimmed AND struck AND switched off — opacity alone is not a
   status (UX-05). */
.editRow[data-hidden] > .editLabel,
.group[data-hidden] > .editHead > .editLabel { opacity: 0.55; text-decoration: line-through; }
.group[data-hidden] > .items { opacity: 0.55; }

/* The two sanctioned exceptions. */
.group[data-grabbed], .editRow[data-grabbed] { outline: 2px solid var(--edge); outline-offset: 2px; }
.group[data-dragging], .editRow[data-dragging] { background: var(--surf-1); box-shadow: var(--clay-4); }
.group[data-recovery] { border-color: var(--edge); background: color-mix(in srgb, var(--edge) 8%, transparent); }

/* A hit-area floor for the rows edit mode adds, local by decision (#1466 OQ-3):
   a global `--ctl-h: 44px` moves rendered geometry on every coarse profile in
   both hosts and belongs in its own change. The CONTROLS, not only their
   containers — the row grew and the buttons inside it did not, so the floor was
   met by the thing nobody taps. */
/* `(max-width: 860px)` as well as `(pointer: coarse)`, and the second half is
   what MEASURED it: ruling 50100 row 7 asks for touch-sized handles in the
   mobile drawer, and a phone-width browser window is not a coarse pointer — the
   grip rendered 36x30 at 320, 375 and 430px with the coarse rule in force and
   not matching. The breakpoint is the shell's own (AppShell.module.css), so the
   floor arrives exactly when the sidebar becomes a drawer. */
@media (pointer: coarse), (max-width: 860px) {
  .readRow [data-testid='customize-row-menu'] { opacity: 1; }
  .editHead, .editRow, .grip, .editEmpty, .editDisclosure { min-height: 44px; }
  .grip { min-width: 44px; }
  .editHead button, .editRow button,
  .editHead [role="switch"], .editRow [role="switch"] { min-width: 44px; min-height: 44px; }
  .editName { min-height: 44px; }
  /* The READ rows' floor is the kit's since #2624: `NavigationDestination`
     takes `max(28px, var(--ctl-h-floor))`, as the section head takes the
     floor in `NavigationSectionHead.module.css`. */
}
